import {createContext, memo, type SetStateAction, useCallback, useMemo, useState} from 'react'

import type {ColumnModel} from '../../models/column-model'

const DialogType = {
  AddColumn: 'add-column',
  IssueFields: 'issue-fields',
  RenameField: 'rename-field',
} as const

type DialogType = (typeof DialogType)[keyof typeof DialogType]

type RenameSource = 'quick-add' | 'issue-fields-dialog'

type RenameState = {
  column: ColumnModel
  issueFieldId: number
  source: RenameSource
  onRenameConfirm?: () => Promise<void>
  onRenameCancel?: () => void
}

type DialogRename = {
  type: typeof DialogType.RenameField
  renameState: RenameState
}

type DialogIssueFields = {
  type: typeof DialogType.IssueFields
}

type DialogAddColumn = {
  type: typeof DialogType.AddColumn
}

type AddFieldDialogKind = DialogRename | DialogIssueFields | DialogAddColumn

type AddFieldModalContextType = {
  /** setter for controlling modal visibility */
  setShowAddFieldModal: React.Dispatch<SetStateAction<boolean>>
  /** boolean for whether the modal is open */
  showAddFieldModal: boolean
  /** current dialog state within the modal (only valid when showAddFieldModal is true) */
  dialog: AddFieldDialogKind | null
  /** setter for updating the dialog state */
  setDialog: React.Dispatch<SetStateAction<AddFieldDialogKind | null>>
  /** opens the rename dialog with the provided state */
  openRenameDialog: (renameState: RenameState) => void
  /** closes the modal and resets all state */
  closeAddColumnModal: () => void
  /** handles rename confirmation including navigation */
  handleRenameConfirm: () => Promise<void>
  /** handles rename cancellation including navigation */
  handleRenameCancel: () => void
}

export const AddFieldModalContext = createContext<AddFieldModalContextType | null>(null)

export const AddFieldModalContextProvider = memo(function AddFieldModalContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [showAddFieldModal, setShowAddFieldModal] = useState(false)
  const [dialog, setDialog] = useState<AddFieldDialogKind | null>(null)

  const openRenameDialog = useCallback((renameState: RenameState) => {
    setDialog({type: DialogType.RenameField, renameState})
    setShowAddFieldModal(true)
  }, [])

  const closeAddColumnModal = useCallback(() => {
    setDialog(null)
    setShowAddFieldModal(false)
  }, [])

  const handleRenameConfirm = useCallback(async () => {
    if (dialog?.type !== DialogType.RenameField) return

    const {renameState} = dialog
    if (renameState.onRenameConfirm) {
      await renameState.onRenameConfirm()
    }

    if (renameState.source === 'quick-add') {
      closeAddColumnModal()
    } else if (renameState.source === 'issue-fields-dialog') {
      setDialog({type: DialogType.IssueFields})
    }
  }, [dialog, closeAddColumnModal])

  const handleRenameCancel = useCallback(() => {
    if (dialog?.type !== DialogType.RenameField) return

    const {renameState} = dialog
    if (renameState.onRenameCancel) {
      renameState.onRenameCancel()
    }

    if (renameState.source === 'quick-add') {
      closeAddColumnModal()
    } else if (renameState.source === 'issue-fields-dialog') {
      setDialog({type: DialogType.IssueFields})
    }
  }, [dialog, closeAddColumnModal])

  const contextValue = useMemo<AddFieldModalContextType>(
    () => ({
      setShowAddFieldModal,
      showAddFieldModal,
      dialog,
      setDialog,
      openRenameDialog,
      closeAddColumnModal,
      handleRenameConfirm,
      handleRenameCancel,
    }),
    [showAddFieldModal, dialog, openRenameDialog, closeAddColumnModal, handleRenameConfirm, handleRenameCancel],
  )

  return <AddFieldModalContext.Provider value={contextValue}>{children}</AddFieldModalContext.Provider>
})

export {DialogType}
export type {AddFieldDialogKind, DialogType as DialogTypeValues, RenameSource, RenameState}

try{ AddFieldModalContext.displayName ||= 'AddFieldModalContext' } catch {}
try{ AddFieldModalContextProvider.displayName ||= 'AddFieldModalContextProvider' } catch {}